import type { AccordionContentProps } from "./types";
import { useAccordionItem } from "./AccordionContext";
import { cx } from "@/utils/cx";
import styles from "./Accordion.module.css";

/**
 * Accordion.Content component
 *
 * The collapsible content panel with CSS Grid animation.
 * Uses grid-template-rows for smooth height animation.
 *
 * @example
 * ```tsx
 * <Accordion.Content>
 *   <p>This content collapses smoothly</p>
 * </Accordion.Content>
 * <Accordion.Content forceMount>Always in DOM</Accordion.Content>
 * ```
 */
export function AccordionContent({
  forceMount = false,
  className,
  children,
}: AccordionContentProps) {
  const item = useAccordionItem();

  // If not force mounted and closed, don't render
  if (!forceMount && !item.isOpen) {
    return null;
  }

  return (
    <div
      id={item.contentId}
      role="region"
      aria-labelledby={item.triggerId}
      className={cx(styles.contentWrapper, className)}
      data-accordion-content
      data-state={item.isOpen ? "open" : "closed"}
      hidden={!item.isOpen && forceMount}
    >
      <div className={styles.content}>{children}</div>
    </div>
  );
}
